import type {
  CreateDataObjectModelResponse,
  CreateDataObjectModelReturn,
  CreateDataObjectDetailsModelResponse,
  CreateDataObjectDetailsModelReturn,
  GetDataObjectTypesRaw,
  CreateDataObjectTypesModelReturn,
  CreateDataObjectWorkflowRelationActionsModelResponse,
  CreateDataObjectWorkflowRelationActionsModelReturn,
  CreateDataObjectActivityLogsModelReturn,
  CreateDataObjectActivityLogsModelResponse,
  CreateDataObjectRelatedObjectsModelReturn,
  CreateDataObjectRelatedObjectsModelResponse,
  CreateDataObjectStatusesModelResponse,
  CreateDataObjectStatusesModelReturn,
  CreateDataObjectTimeSeriesModelResponse,
  CreateDataObjectTimeSeriesModelReturn,
  CreateCopySourceObjectsModelResponse,
  CreateCopySourceObjectsModelReturn,
  CreateCopyStepModelResponse,
  CreateCopyStepModelReturn,
  CreateDataObjectAccessibleSectionsModelResponse,
  CreateDataObjectAccessibleSectionsModelReturn,
  CreateDataObjectTypesModelWithTranslationsResponse,
  CreateDataObjectTypesModelWithTranslationsReturn,
  CreateDataObjectFileUrlsModelResponse,
  CreateDataObjectFileUrlsModelReturn,
  GetDataObjectListResponse,
  GetDataObjectListModalReturn,
  CreateDataObjectAccessibleSchemaModelResponse,
  CreateDataObjectAccessibleSchemaModelReturn,
  FiltersByFieldMap,
  DataObjectTypeRaw,
  DataObjectType,
  DataObjectIndicator,
  DataObjectIndicatorResponse,
  GetSelectionTableFieldDataResponse,
  GetSelectionTableFieldDataReturn,
  GetSelectionResponseData,
  GetSelectionResponseReturn
} from "../types/dataObjectsAPIs.types";
import type { GetSurveyTableFieldDataReturn } from "../types/dataObjectsSurveyTable.types";
import type { DataObjectFilterConfigRaw } from "@certa/types/src/dataObjects.types";

const createFilterConfigLookup = (
  filterConfig: DataObjectFilterConfigRaw
): FiltersByFieldMap => {
  const filterFieldMap: FiltersByFieldMap = {};

  if (!filterConfig) return filterFieldMap;

  const {
    multi_select: multiSelectFilters,
    single_select: singleSelectFilters
  } = filterConfig;

  Object.entries(multiSelectFilters ?? {}).forEach(
    ([fieldName, filterSettings]) => {
      filterFieldMap[fieldName] = {
        type: "select",
        options: filterSettings.options,
        optionUrl: filterSettings.option_url
      };
    }
  );

  Object.entries(singleSelectFilters ?? {}).forEach(
    ([fieldName, filterSettings]) => {
      filterFieldMap[fieldName] = {
        type: "single_select",
        options: filterSettings.options,
        optionUrl: filterSettings.option_url
      };
    }
  );

  return filterFieldMap;
};

export const getDataObjectTypesModel = (
  response: GetDataObjectTypesRaw
): CreateDataObjectTypesModelReturn => {
  return response.results.map(dataObject => ({
    id: dataObject.id.toString(),
    tag: dataObject.tag,
    schema: dataObject.schema,
    uiSchema: dataObject.ui_schema
  }));
};

export const createDataObjectTypesModelWithTranslations = (
  response: CreateDataObjectTypesModelWithTranslationsResponse
): CreateDataObjectTypesModelWithTranslationsReturn => {
  return response.object_types.map(dataObject => ({
    id: dataObject.id.toString(),
    tag: dataObject.tag,
    translatedUiSchema: dataObject.translated_ui_schema,
    schema: dataObject.schema,
    uiSchema: dataObject.ui_schema,
    filtersByField: createFilterConfigLookup(dataObject.filter_config)
  }));
};

export const createDataObjectModel = (
  response: CreateDataObjectModelResponse
): CreateDataObjectModelReturn => {
  return {
    objects: Object.fromEntries(
      Object.entries(response.objects).map(([objectType, dataObject]) => [
        objectType,
        {
          id: dataObject.id,
          name: dataObject.name,
          kindId: dataObject.object_kind_id,
          data: dataObject.data
        }
      ])
    ),
    definitions: response.definitions
  };
};

const dataObjectTypeModelCreator = (
  dataObjectRaw: DataObjectTypeRaw
): DataObjectType => {
  return {
    id: dataObjectRaw.id,
    tag: dataObjectRaw.tag,
    schema: dataObjectRaw.schema,
    uiSchema: dataObjectRaw.ui_schema,
    createdAt: dataObjectRaw.created_at,
    updatedAt: dataObjectRaw.updated_at
  };
};

// Reusable function to transform indicators from API response to model format
const transformIndicators = (
  indicators: Record<string, DataObjectIndicatorResponse> | null | undefined
): Record<string, DataObjectIndicator> | null => {
  return indicators
    ? Object.fromEntries(
        Object.entries(indicators).map(([key, value]) => [
          key,
          {
            showLabel: value.show_label,
            label: value.label,
            description: value.description,
            color: value.color,
            ...Object.fromEntries(
              Object.entries(value).filter(
                ([key]) =>
                  key.startsWith("label_") || key.startsWith("description_")
              )
            )
          }
        ])
      )
    : null;
};

export const createDataObjectDetailsModel = (
  response: CreateDataObjectDetailsModelResponse
): CreateDataObjectDetailsModelReturn => {
  const {
    object_instances: objectInstances = [],
    object_types: objectTypes = [],
    object_workflow_mappings: objectWorkflowMappings = [],
    badges: objectBadges = [],
    object_statuses: objectStatuses = [],
    matched_objects: matchedObjects = []
  } = response.results;

  return {
    next: response.next,
    previous: response.previous,
    count: response.count,
    matchedObjects: matchedObjects.map(object => ({
      uid: object._source.uid,

      highlights: {
        name: object.highlight?.name
      },
      innerHits: {
        data: object.inner_hits?.data?.map(innerHit => ({
          tag: innerHit._source.tag,
          highlights: innerHit.highlight?.["data.value"],
          value: innerHit._source.value
        }))
      }
    })),
    badges: objectBadges.map(badge => ({
      tag: badge.tag,
      name: badge.name,
      helpText: badge.help_text,
      fieldTag: badge.field_tag,
      uiSpecs: badge.ui_specs
    })),
    objectInstances: objectInstances.map(instance => {
      const status = objectStatuses?.find(
        status => status.id === instance.status
      );
      return {
        id: instance.id, // TODO: Remove this once create workflow supports uid for object
        uid: instance.uid,
        name: instance.name,
        logo: instance.logo,
        data: instance.data,
        createdAt: instance.created_at,
        updatedAt: instance.updated_at,
        objectType: instance.object_type,
        workflowMappings: instance.workflow_mappings,
        outgoingRelationships: instance.outgoing_relationships,
        incomingRelationships: instance.incoming_relationships,
        appliedBadges: instance.applied_badges?.map(badge => ({
          tag: badge.tag,
          createdAt: badge.created_at
        })),
        indicators: transformIndicators(instance.indicators),
        status: status
          ? {
              id: status.id,
              tag: status.tag,
              name: status.name,
              colorCode: status.color_code
            }
          : undefined
      };
    }),
    objectWorkflowMappings: objectWorkflowMappings.map(mapping => ({
      id: mapping.id,
      workflow: mapping.workflow,
      objectReference: mapping.object_reference
    })),
    objectTypes: objectTypes.map(dataObjectTypeModelCreator)
  };
};

export const createDataObjectWorkflowRelationActionsModel = (
  response: CreateDataObjectWorkflowRelationActionsModelResponse
): CreateDataObjectWorkflowRelationActionsModelReturn => {
  const { results = [] } = response;

  return results.map(result => ({
    objectTypeTag: result.object_type_tag,
    relation: result.relation,
    workflowKindTag: result.workflow_kind_tag,
    restrictedForOwnerInGroup: result.extra?.restricted_for_owner_in_group,
    allowedStatusTags: result.extra?.allowed_status_tags,
    redirectLinkUrl: result.extra?.redirect_link_url,
    redirectLinkText: result.extra?.redirect_link_text,
    isDisabled: result.is_disabled,
    allowMultipleWorkflows: result.extra?.allow_multiple_active_workflows
  }));
};

export const createDataObjectActivityLogsModel = (
  response: CreateDataObjectActivityLogsModelResponse
): {
  significantEventsLogs: CreateDataObjectActivityLogsModelReturn;
  schedulerEventsLogs: CreateDataObjectActivityLogsModelReturn;
} => {
  const results = response.map(result => ({
    id: result.id,
    objectUid: result.object_uid,
    operationType: result.operation_type,
    actorType: result.actor_type,
    actorDetails: result.actor_details,
    timestamp: result.timestamp,
    oldData: result.old_data,
    newData: result.new_data,
    badges: result.badges
  }));

  const significantEventsLogs: CreateDataObjectActivityLogsModelReturn = [];
  const schedulerEventsLogs: CreateDataObjectActivityLogsModelReturn = [];

  results?.forEach(log => {
    if (log.actorType === "SCHEDULER") schedulerEventsLogs.push(log);
    else significantEventsLogs.push(log);
  });

  return {
    significantEventsLogs,
    schedulerEventsLogs
  };
};

export const createDataObjectRelatedObjectsModel = (
  response: CreateDataObjectRelatedObjectsModelResponse
): CreateDataObjectRelatedObjectsModelReturn => {
  return response.map(result => ({
    id: result.id,
    name: result.name,
    uid: result.uid,
    status: !!result.status
      ? {
          id: result.status.id,
          tag: result.status.tag,
          name: result.status.name,
          colorCode: result.status.color_code
        }
      : null,
    data: result.data,
    createdAt: result.created_at,
    updatedAt: result.updated_at
  }));
};

export const createDataObjectStatusesModel = (
  response: CreateDataObjectStatusesModelResponse
): CreateDataObjectStatusesModelReturn => {
  return response?.map(result => ({
    id: result.id,
    tag: result.tag,
    name: result.name,
    colorCode: result.color_code
  }));
};

export const createDataObjectTimeSeriesModel = (
  response: CreateDataObjectTimeSeriesModelResponse
): CreateDataObjectTimeSeriesModelReturn => {
  return response;
};

export const createCopySourceObjectsModel = (
  response: CreateCopySourceObjectsModelResponse
): CreateCopySourceObjectsModelReturn => {
  const {
    object_instances: objectInstances = [],
    object_types: objectTypes = [],
    object_statuses: objectStatuses = []
  } = response.results;

  return {
    next: response.next,
    previous: response.previous,
    count: response.count,
    objectInstances: objectInstances.map(instance => {
      const status = objectStatuses?.find(
        status => status.id === instance.status
      );
      return {
        id: instance.id, // TODO: Remove this once create workflow supports uid for object
        uid: instance.uid,
        name: instance.name,
        logo: instance.logo,
        data: instance.data,
        createdAt: instance.created_at,
        updatedAt: instance.updated_at,
        objectType: instance.object_type,
        workflowMappings: instance.workflow_mappings,
        outgoingRelationships: instance.outgoing_relationships,
        incomingRelationships: instance.incoming_relationships,
        appliedBadges: instance.applied_badges?.map(badge => ({
          tag: badge.tag,
          createdAt: badge.created_at
        })),
        status: status
          ? {
              id: status.id,
              tag: status.tag,
              name: status.name,
              colorCode: status.color_code
            }
          : undefined
      };
    }),
    objectTypes: objectTypes.map(objectType => ({
      id: objectType.id,
      tag: objectType.tag,
      schema: objectType.schema,
      createdAt: objectType.created_at,
      updatedAt: objectType.updated_at,
      uiSchema: objectType.ui_schema
    }))
  };
};

export const createCopyStepModel = (
  response: CreateCopyStepModelResponse
): CreateCopyStepModelReturn => {
  return response;
};

export const createDataObjectAccessibleSectionsModel = (
  response: CreateDataObjectAccessibleSectionsModelResponse
): CreateDataObjectAccessibleSectionsModelReturn => {
  return response;
};

export const createDataObjectFileUrlsModel = (
  response: CreateDataObjectFileUrlsModelResponse
): CreateDataObjectFileUrlsModelReturn => {
  return Object.fromEntries(
    Object.entries(response).map(([key, value]) => [
      key,
      value.map(item => ({
        attachment: item.attachment,
        submittedById: item.submitted_by_id
      }))
    ])
  );
};

export const createDataObjectListModel = (
  response: GetDataObjectListResponse
): GetDataObjectListModalReturn => {
  return {
    count: response.count,
    next: response.next,
    previous: response.previous,
    results: response.results.map(result => ({
      objectId: result.object_id,
      objectUid: result.object_uid,
      name: result.name,
      createdAt: result.created_at,
      updatedAt: result.updated_at,
      data: result.data,
      logo: result.logo,
      status: result.status
        ? {
            id: result.status.id,
            tag: result.status.tag,
            name: result.status.name,
            colorCode: result.status.color_code
          }
        : undefined,
      workflowMappings: result.workflow_mappings.map(mapping => ({
        workflow: mapping.workflow,
        objectReference: mapping.object_reference
      })),
      appliedBadges: result.applied_badges.map(badge => ({
        tag: badge.tag,
        createdAt: badge.created_at
      })),
      indicators: transformIndicators(result.indicators)
    })),
    objectType: {
      id: response.object_type.id,
      tag: response.object_type.tag,
      schema: response.object_type.schema,
      uiSchema: response.object_type.ui_schema,
      filterConfig: response.object_type.filter_config
    }
  };
};

export const createDataObjectAccessibleSchemaModel = (
  response: CreateDataObjectAccessibleSchemaModelResponse
): CreateDataObjectAccessibleSchemaModelReturn => {
  return {
    schema: response,
    accessibleFields: Object.keys(response.properties)
  };
};

export const createSurveyTableFieldDataModel = (
  response: GetDataObjectListResponse
): GetSurveyTableFieldDataReturn => {
  return {
    count: response.count,
    next: response.next,
    previous: response.previous,
    results: response.results.map(result => ({
      objectId: result.object_id,
      objectUid: result.object_uid,
      name: result.name,
      createdAt: result.created_at,
      updatedAt: result.updated_at,
      data: result.data,
      logo: result.logo,
      status: result.status
        ? {
            id: result.status.id,
            tag: result.status.tag,
            name: result.status.name,
            colorCode: result.status.color_code
          }
        : undefined
    })),
    objectType: {
      id: response.object_type.id,
      tag: response.object_type.tag,
      schema: response.object_type.schema,
      uiSchema: response.object_type.ui_schema,
      filterConfig: response.object_type.filter_config
    }
  };
};

export const createSelectionTableFieldDataModel = (
  response: GetSelectionTableFieldDataResponse
): GetSelectionTableFieldDataReturn => {
  const transformed = {
    count: response.count,
    next: response.next,
    previous: response.previous,
    results: response.results.map(result => ({
      ...result.data,
      id: result.object_id, /// Here, the object_id is stored as ID as we get selected IDs from another API, and it is used to show selected rows in the table.
      objectId: result.object_id,
      objectUid: result.object_uid,
      uid: result.object_uid,
      logo: result.logo,
      name: result.name,
      createdAt: result.created_at,
      updatedAt: result.updated_at,
      data: result.data,
      status: result.status
        ? {
            tag: result.status.tag,
            name: result.status.name,
            colorCode: result.status.color_code
          }
        : undefined
    })),
    objectType: {
      id: response.object_type.id,
      tag: response.object_type.tag,
      schema: response.object_type.schema,
      uiSchema: response.object_type.ui_schema,
      filterConfig: createFilterConfigLookup(response.object_type.filter_config)
    }
  };

  return transformed;
};

export const createSelectionResponseModel = (
  response: GetSelectionResponseData
): GetSelectionResponseReturn => {
  const transformed = {
    id: response.id,
    objectIds: response.object_ids ?? []
  };

  return transformed;
};
